/**
 * LinkedEncountersPanel Component
 * 
 * Displays encounters linked to an oncology episode of care and allows
 * linking/unlinking encounters from the episode side.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Divider,
  Tooltip,
  Paper
} from '@mui/material';
import {
  EventNote as EncounterIcon,
  Link as LinkIcon,
  LinkOff as UnlinkIcon,
  Search as SearchIcon,
  OpenInNew as OpenIcon,
  Add as AddIcon,
  LocalHospital as HospitalIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useUserData } from '../../hooks/useUserData';
import {
  linkEncounterToEpisode,
  unlinkEncounterFromEpisode
} from '../../services/episodeOfCareService';
import { getAuthToken } from '../../utils/authUtils';

// Encounter type configuration
const encounterTypeConfig = {
  'inpatient': { label: 'Inpatient', color: 'error' },
  'outpatient': { label: 'Outpatient', color: 'primary' },
  'emergency': { label: 'Emergency', color: 'warning' },
  'telehealth': { label: 'Telehealth', color: 'info' },
  'surgery': { label: 'Surgery', color: 'secondary' },
  'observation': { label: 'Observation', color: 'default' }
};

const LinkedEncountersPanel = ({
  episodeId,
  patientId,
  linkedEncounterIds = [],
  onEncounterLinked, // Callback when an encounter is linked
  onEncounterUnlinked, // Callback when an encounter is unlinked
  readOnly = false
}) => {
  const navigate = useNavigate();
  const { defaultFacility } = useUserData();
  const facilityId = defaultFacility?.id;

  // State
  const [linkedEncounters, setLinkedEncounters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [availableEncounters, setAvailableEncounters] = useState([]);
  const [loadingEncounters, setLoadingEncounters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionLoading, setActionLoading] = useState(null); // ID of encounter being actioned

  // Fetch linked encounter details
  const fetchLinkedEncounters = useCallback(async () => {
    if (!linkedEncounterIds || linkedEncounterIds.length === 0) {
      setLinkedEncounters([]);
      return;
    }

    try {
      setLoading(true);
      const token = await getAuthToken();
      const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';

      // Fetch encounter details for each linked ID
      const encounterPromises = linkedEncounterIds.map(async (encounterId) => {
        try {
          const response = await fetch(`${API_BASE}/internal/encounters/${encounterId}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          if (response.ok) {
            return await response.json();
          }
          return null;
        } catch {
          return null;
        }
      });

      const results = await Promise.all(encounterPromises);
      setLinkedEncounters(results.filter(e => e !== null));
    } catch (err) {
      console.error('Error fetching linked encounters:', err);
      setError('Failed to load linked encounters');
    } finally {
      setLoading(false);
    }
  }, [linkedEncounterIds]);

  useEffect(() => {
    fetchLinkedEncounters();
  }, [fetchLinkedEncounters]);

  // Fetch available encounters for linking
  const fetchAvailableEncounters = useCallback(async () => {
    if (!patientId || !facilityId) return;

    try {
      setLoadingEncounters(true);
      const token = await getAuthToken();
      const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';

      const response = await fetch(
        `${API_BASE}/internal/encounters?patientId=${patientId}&facilityId=${facilityId}&limit=50`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        // Filter out already linked encounters
        const unlinked = (data.data || data || []).filter(
          enc => !linkedEncounterIds.includes(enc.id) && !enc.oncologyEpisodeId
        );
        setAvailableEncounters(unlinked);
      } else {
        setAvailableEncounters([]);
      }
    } catch (err) {
      console.error('Error fetching available encounters:', err);
      setAvailableEncounters([]);
    } finally {
      setLoadingEncounters(false);
    }
  }, [patientId, facilityId, linkedEncounterIds]);

  // Handle opening the link dialog
  const handleOpenLinkDialog = () => {
    setSearchTerm('');
    fetchAvailableEncounters();
    setLinkDialogOpen(true);
  };

  // Handle linking an encounter
  const handleLinkEncounter = async (encounter) => {
    try {
      setActionLoading(encounter.id);
      setError(null);
      await linkEncounterToEpisode(episodeId, encounter.id);
      setLinkedEncounters(prev => [...prev, encounter]);
      setAvailableEncounters(prev => prev.filter(e => e.id !== encounter.id));
      setLinkDialogOpen(false);
      onEncounterLinked?.(encounter.id);
    } catch (err) {
      console.error('Error linking encounter:', err);
      setError('Failed to link encounter');
    } finally {
      setActionLoading(null);
    }
  };

  // Handle unlinking an encounter
  const handleUnlinkEncounter = async (encounterId) => {
    try {
      setActionLoading(encounterId);
      setError(null);
      await unlinkEncounterFromEpisode(episodeId, encounterId);
      setLinkedEncounters(prev => prev.filter(e => e.id !== encounterId));
      onEncounterUnlinked?.(encounterId);
    } catch (err) {
      console.error('Error unlinking encounter:', err);
      setError('Failed to unlink encounter');
    } finally {
      setActionLoading(null);
    }
  };

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch {
      return dateString;
    }
  };

  // Filter available encounters by search term
  const filteredEncounters = availableEncounters.filter(enc => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      (enc.type?.toLowerCase() || '').includes(search) ||
      (enc.status?.toLowerCase() || '').includes(search) ||
      (enc.fin?.toLowerCase() || '').includes(search) ||
      (enc.chiefComplaint?.toLowerCase() || '').includes(search)
    );
  });

  // Get encounter type config
  const getEncounterTypeConfig = (type) => {
    return encounterTypeConfig[type] || { label: type || 'Unknown', color: 'default' };
  };

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EncounterIcon color="primary" />
            <Typography variant="subtitle1" fontWeight="bold">
              Linked Encounters ({linkedEncounters.length})
            </Typography>
          </Box>
          {!readOnly && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleOpenLinkDialog}
            >
              Link Encounter
            </Button>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Loading state */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : linkedEncounters.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
            <HospitalIcon sx={{ fontSize: 36, color: 'text.secondary', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No encounters linked to this episode
            </Typography>
            {!readOnly && (
              <Button
                size="small"
                startIcon={<LinkIcon />}
                onClick={handleOpenLinkDialog}
                sx={{ mt: 1 }}
              >
                Link an Encounter
              </Button>
            )}
          </Paper>
        ) : (
          <List dense disablePadding>
            {linkedEncounters.map((encounter, index) => {
              const typeConfig = getEncounterTypeConfig(encounter.type);
              
              return (
                <React.Fragment key={encounter.id}>
                  {index > 0 && <Divider />}
                  <ListItem sx={{ py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <EncounterIcon color={typeConfig.color} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" fontWeight="medium">
                            {encounter.fin || `Encounter ${encounter.id.substring(0, 8)}`}
                          </Typography>
                          <Chip
                            size="small"
                            label={typeConfig.label}
                            color={typeConfig.color}
                            sx={{ height: 20, fontSize: '0.65rem' }}
                          />
                          {encounter.status && (
                            <Chip
                              size="small"
                              label={encounter.status}
                              variant="outlined"
                              sx={{ height: 20, fontSize: '0.65rem' }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(encounter.createdAt || encounter.admissionTime)} 
                          {encounter.chiefComplaint && ` • ${encounter.chiefComplaint}`}
                        </Typography>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title="View Encounter">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/encounters/${encounter.id}`)}
                        >
                          <OpenIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {!readOnly && (
                        <Tooltip title="Unlink from Episode">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleUnlinkEncounter(encounter.id)}
                            disabled={actionLoading === encounter.id}
                          >
                            {actionLoading === encounter.id ? (
                              <CircularProgress size={16} />
                            ) : (
                              <UnlinkIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}
                    </ListItemSecondaryAction>
                  </ListItem>
                </React.Fragment>
              );
            })}
          </List>
        )}
      </CardContent>

      {/* Link Encounter Dialog */}
      <Dialog
        open={linkDialogOpen}
        onClose={() => setLinkDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LinkIcon color="primary" />
            Link Encounter to Episode
          </Box>
        </DialogTitle>
        <DialogContent>
          {/* Search */}
          <TextField
            fullWidth
            size="small"
            placeholder="Search encounters..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
            sx={{ mb: 2, mt: 1 }}
          />

          {loadingEncounters ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : filteredEncounters.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <HospitalIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography variant="body1" color="text.secondary">
                No available encounters to link
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                All patient encounters are either already linked or don't exist.
              </Typography>
            </Box>
          ) : (
            <List sx={{ maxHeight: 400, overflow: 'auto' }}>
              {filteredEncounters.map((encounter, index) => {
                const typeConfig = getEncounterTypeConfig(encounter.type);

                return (
                  <React.Fragment key={encounter.id}>
                    {index > 0 && <Divider />}
                    <ListItem 
                      button
                      onClick={() => handleLinkEncounter(encounter)}
                      disabled={actionLoading === encounter.id}
                    >
                      <ListItemIcon>
                        <EncounterIcon color={typeConfig.color} />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2">
                              {encounter.fin || `Encounter ${encounter.id.substring(0, 8)}`}
                            </Typography>
                            <Chip
                              size="small"
                              label={typeConfig.label}
                              color={typeConfig.color}
                              sx={{ height: 18, fontSize: '0.65rem' }}
                            />
                          </Box>
                        }
                        secondary={
                          <Box component="span">
                            <Typography variant="caption" color="text.secondary" component="span">
                              {formatDate(encounter.createdAt || encounter.admissionTime)}
                            </Typography>
                            {encounter.chiefComplaint && (
                              <Typography variant="caption" color="text.secondary" component="span">
                                {' • '}{encounter.chiefComplaint}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                      {actionLoading === encounter.id && (
                        <CircularProgress size={20} sx={{ ml: 1 }} />
                      )}
                    </ListItem>
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default LinkedEncountersPanel;
